/**
 * useLandingStats — fetches live platform stats from /stats on mount, falls
 * back to sensible defaults if the request fails, and returns rounded
 * display values ready for the counters.
 */

import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api/apiClient';
import { FALLBACK_STATS, roundStat, type PlatformStats, type RoundedStat } from '../common/stats';

export interface LandingStats {
  writers: RoundedStat;
  journals: RoundedStat;
  subscriptions: RoundedStat;
  paidOut: RoundedStat;
}

export function useLandingStats(): LandingStats {
  const [stats, setStats] = useState<PlatformStats | null>(null);

  useEffect(() => {
    apiClient
      .get<{ success: boolean; data: PlatformStats }>('/stats')
      .then((json) => {
        if (json.success) setStats(json.data);
      })
      .catch(() => {
        // Keep fallbacks; swallowing is intentional — the landing page
        // should never hard-fail because a stats endpoint blipped.
      });
  }, []);

  const src = stats ?? FALLBACK_STATS;
  return {
    writers: roundStat(src.writers),
    journals: roundStat(src.journals),
    subscriptions: roundStat(src.subscriptions),
    paidOut: roundStat(src.paidOut),
  };
}
